import type { DimensionDescriptor, MeasureDrillDownInfo } from "../../../../../../../../../api/types/svTypes.ts";
import type { FieldWithOrder, FieldWithSorting } from "../../../../../hooks/FieldWithOrder";
import type { DrillDownLedgerColumnsSetup, LedgerBucket } from "./drillDownLedgerColumnsStorage";
import { getExtensionLedger } from "./extensionLedgers";

export interface LedgerColumnsSetup {
  selectedFields: FieldWithOrder[];
  sortedFields: FieldWithSorting[];
}

const ALLOCATED_ONLY_DIMENSIONS = ["InvestorTypeCode", "InvestorName", "Shares"];

const sortByOrder = <T extends { order: number }>(items: T[]): T[] => {
  return [...items].sort((a, b) => a.order - b.order).map((item, index) => ({ ...item, order: index }));
};

const cloneSetup = (setup: LedgerColumnsSetup): LedgerColumnsSetup => {
  return {
    selectedFields: sortByOrder(setup.selectedFields),
    sortedFields: sortByOrder(setup.sortedFields),
  };
};

export const getLedgerBucket = (info: MeasureDrillDownInfo): LedgerBucket => {
  const extensionLedger = getExtensionLedger(info.table);
  if (extensionLedger !== undefined) {
    return extensionLedger.bucket;
  }

  if (info.allocated === true) {
    return "allocated";
  }

  return "ledger";
};

export const buildDefaultSetup = (
  defaultDimensions: string[],
  withAllocation: boolean,
  allFields: DimensionDescriptor[]
): LedgerColumnsSetup => {
  const selectedFields = defaultDimensions
    .filter((name) => (withAllocation ? true : !ALLOCATED_ONLY_DIMENSIONS.includes(name)))
    .map<FieldWithOrder | undefined>((name, index) => {
      const field = allFields.find((dimension) => dimension.name === name);
      if (field === undefined) {
        return undefined;
      }

      return {
        field,
        order: index,
        canBeRemoved: name !== "EntryNo",
      };
    })
    .filter((field): field is FieldWithOrder => field !== undefined);

  const entryNoField = selectedFields.find((field) => field.field.name === "EntryNo");
  const sortedFields =
    entryNoField === undefined
      ? []
      : [
          {
            field: entryNoField.field,
            order: 0,
            sortAsc: true,
            canBeRemoved: entryNoField.canBeRemoved,
          },
        ];

  return {
    selectedFields: sortByOrder(selectedFields),
    sortedFields,
  };
};

export const restorePersistedSetup = (
  persistedSetup: DrillDownLedgerColumnsSetup | undefined,
  allDimensions: DimensionDescriptor[],
  fallbackDefaultSetup: LedgerColumnsSetup
): LedgerColumnsSetup => {
  if (!persistedSetup) {
    return cloneSetup(fallbackDefaultSetup);
  }

  const selectedFields = persistedSetup.selectedColumnNames
    .map<FieldWithOrder | undefined>((dimensionName, index) => {
      const field = allDimensions.find((dimension) => dimension.name === dimensionName);
      if (field === undefined) {
        return undefined;
      }

      return {
        field,
        order: index,
        canBeRemoved: dimensionName !== "EntryNo",
      };
    })
    .filter((field): field is FieldWithOrder => field !== undefined);

  if (selectedFields.length === 0) {
    return cloneSetup(fallbackDefaultSetup);
  }

  const selectedFieldsByName = selectedFields.reduce<Record<string, FieldWithOrder>>((result, field) => {
    result[field.field.name] = field;
    return result;
  }, {});

  const sortedFields = sortByOrder(persistedSetup.sorting)
    .map<FieldWithSorting | undefined>((sorting, index) => {
      const selectedField = selectedFieldsByName[sorting.dimensionName];
      if (selectedField === undefined) {
        return undefined;
      }

      return {
        field: selectedField.field,
        sortAsc: sorting.sortAsc,
        order: index,
        canBeRemoved: selectedField.canBeRemoved,
      };
    })
    .filter((sorting): sorting is FieldWithSorting => sorting !== undefined);

  return {
    selectedFields: sortByOrder(selectedFields),
    sortedFields: sortByOrder(sortedFields),
  };
};

const getSelectedSignature = (setup: LedgerColumnsSetup): string => {
  return sortByOrder(setup.selectedFields)
    .map((field) => field.field.name)
    .join("|");
};

const getSortingSignature = (setup: LedgerColumnsSetup): string => {
  return sortByOrder(setup.sortedFields)
    .map((sorting) => `${sorting.field.name}:${sorting.sortAsc ? "asc" : "desc"}`)
    .join("|");
};

export const isCustomSetup = (currentSetup: LedgerColumnsSetup, defaultSetup: LedgerColumnsSetup): boolean => {
  return (
    getSelectedSignature(currentSetup) !== getSelectedSignature(defaultSetup) ||
    getSortingSignature(currentSetup) !== getSortingSignature(defaultSetup)
  );
};
